// Main entry point for Valentine Farm Game
// Using Three.js for 3D Township-style rendering

import { Game } from './Engine/Game';
import { ExperienceService } from './services/ExperienceService';
import { Router } from './Router';

// Wait for DOM to be ready
document.addEventListener('DOMContentLoaded', async () => {
    const container = document.getElementById('game-container');

    if (!container) {
        console.error('Game container not found!');
        return;
    }

    const router = new Router();
    const route = router.getRoute();
    console.log('Routing:', route);

    switch (route.type) {
        case 'GAME':
            await initGame(container, route.subdomain, route.shareId);
            break;
        case 'SUCCESS':
            initSuccess(container, route.subdomain, route.token, route.id);
            break;
        case 'EDIT':
            initLanding(container, route.id, route.token);
            break;
        case 'CREATE':
        case 'LANDING':
        default:
            initLanding(container);
            break;
    }
});

async function initGame(container: HTMLElement, subdomain: string, _shareId?: string) {
    console.log(`🔍 Checking world for subdomain: ${subdomain}`);

    // Show loading state
    container.innerHTML = `
        <div style="
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            display: flex; justify-content: center; align-items: center;
            background: linear-gradient(135deg, #fff0f5 0%, #fff 100%);
            font-family: 'Outfit', sans-serif; flex-direction: column;
        ">
            <div style="font-size: 24px; color: #ff6b9d; font-weight: 600;">Entering World... 🌍</div>
        </div>
    `;

    try {
        const result = await ExperienceService.getExperienceBySubdomain(subdomain);

        if (result.success && result.config) {
            console.log(`🎮 Loading Game for: ${result.config.subdomain}`);
            // Update document title
            document.title = `For ${result.config.partnerName} - CupidPlan`;

            // Clear loading
            container.innerHTML = '';
            // Initialize the Game with config
            document.body.classList.add('game-mode'); // Lock scroll for game
            Game.create(container, result.config);

            // If shareId, we could pass it to Game.create too, 
            // but currently Game reads query params itself or we should pass it in config?
            // Ideally, Game should take "initialState" or "shareId" as arg.
            // For now, let's leave it as Game likely checking URL or we add it later.

        } else {
            // 404
            render404(container, subdomain);
        }
    } catch (e) {
        console.error('Failed to load experience:', e);
        render404(container, subdomain);
    }
}

function initLanding(container: HTMLElement, editId?: string, editToken?: string) {
    console.log('🏠 Loading Creator Landing Page');
    import('./creator/LandingPage').then(({ LandingPage }) => {
        const landing = new LandingPage(container);
        if (editId && editToken) {
            landing.enableEditMode(editId, editToken);
        }
        landing.render();
    });
}

function initSuccess(container: HTMLElement, subdomain: string, token?: string, id?: string) {
    console.log('🎉 Loading Success Page for subdomain:', subdomain);
    import('./creator/SuccessPage').then(({ SuccessPage }) => {
        const successPage = new SuccessPage(container, subdomain, token, id);
        successPage.init();
    });
}

function render404(container: HTMLElement, subdomain: string) {
    container.innerHTML = `
        <div style="
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            display: flex; justify-content: center; align-items: center;
            background: #fff; color: #333; font-family: 'Outfit', sans-serif;
            flex-direction: column; text-align: center; padding: 20px;
        ">
            <h1 style="color: #ff6b9d; font-size: 40px; margin-bottom: 20px;">404 💔</h1>
            <p style="font-size: 18px; color: #666; margin-bottom: 30px;">
                The world <strong>${subdomain}</strong> doesn't exist yet.
            </p>
            <a href="https://cupidplan.com" style="
                padding: 12px 30px; background: #ff6b9d; color: white;
                text-decoration: none; border-radius: 30px; font-weight: 600;
            ">Create Your Own</a>
        </div>
    `;
}
